import { getVizColors, vizColor_hoverOpacity } from '@opengov/capital-style';
import {
  calcDimensions,
  createColorScale,
  ICoordinateSystem,
  IMargin,
  Orientation,
  SortOrder
} from '@opengov/data-viz';
import { LegendSortOptions } from '@opengov/reporting-types';
import isBlank from '@sedan-utils/is-blank';
import Color from 'color';
import { ScaleOrdinal } from 'd3-scale';

import { DEFAULT_CHART_HEIGHT, DEFAULT_CHART_MIN_BANDWIDTH, DEFAULT_CHART_WIDTH } from './constants';
import { IChartDimensions, IChartMargin, IColorVariants } from './types';

export { createColorScale };

export const makeCoordinateSystems = (
  margin: IChartMargin,
  width: number = DEFAULT_CHART_WIDTH
): { coordinateSystem: ICoordinateSystem; margin: IChartMargin; dimensions: IChartDimensions } => {
  const newMargin = {
    ...margin
  };
  // this is so reporting-app centric :(
  const coordinateSystem = { x: 0, y: 0, width, height: DEFAULT_CHART_HEIGHT };
  return {
    coordinateSystem,
    margin: newMargin,
    dimensions: calcDimensions(margin, coordinateSystem.width, coordinateSystem.height)
  };
};

export const colorMap = (
  colorScale: ScaleOrdinal<string, any>,
  categories: string[],
  categoryToCustomColor: { [key: string]: string } = {}
): { [key: string]: IColorVariants } => {
  const colors = {};

  // if presentationConfiguration is empty,
  // create a colorMap without checking for custom values
  if (isBlank(categoryToCustomColor)) {
    categories.forEach((category) => {
      colors[category] = colorScale(category);
    });
    return colors;
  } else {
    // if there are values in the presentationConfiguration,
    // create a colorMap that includes both custom and standard colors
    categories.forEach((category) => {
      const customBase = categoryToCustomColor[category];
      if (customBase !== undefined && customBase !== null) {
        const customHighlight = Color(customBase).alpha(vizColor_hoverOpacity).rgb().string();
        colors[category] = {
          base: customBase,
          highlight: customHighlight,
          hover: customBase,
          line: customBase,
          point: customBase
        };
      } else {
        colors[category] = colorScale(category);
      }
    });

    return colors;
  }
};

export const customColorsToHexValueMap = (customColors: {
  [key: string]: { hexValue: string };
}): { [key: string]: string } => {
  const output = {};
  if (customColors) {
    Object.keys(customColors).forEach((key) => {
      output[key] = customColors[key].hexValue;
    });
  }
  return output;
};

export const assembleAllColors = (
  sortedCategories: string[],
  customColorsMap: { [key: string]: string } = {}
): { [key: string]: IColorVariants } => {
  const sortedCategoriesWithOther = [...new Set(sortedCategories)];
  const colorScale = createColorScale<any>().domain(sortedCategories).range(getVizColors(sortedCategories.length));

  return colorMap(colorScale, sortedCategoriesWithOther, customColorsMap);
};

export const ticksLengthOfShowAsPercentOfTotal = (showAsPercentOfTotal) => (showAsPercentOfTotal ? 4 : 8);

export const adjustScalesForMinimumBandwidth = (
  orientation: Orientation,
  margin: IMargin,
  dimensions: IChartDimensions,
  coordinateSystem: ICoordinateSystem,
  createYScaleWithDimensions: (dimensions: IChartDimensions) => any,
  createXScaleWithDimensions: (dimensions: IChartDimensions) => any,
  minBandWidth: number = DEFAULT_CHART_MIN_BANDWIDTH
) => {
  const defaultYScale = createYScaleWithDimensions(dimensions);
  const defaultXScale = createXScaleWithDimensions(dimensions);
  if (orientation === 'horizontal') {
    const bandwidth = defaultYScale.bandwidth();
    if (bandwidth < minBandWidth) {
      const bandwidthFactor = minBandWidth / bandwidth;
      const updatedDimensions = { ...dimensions, height: dimensions.height * bandwidthFactor };
      const verticalMargin = margin.top + margin.bottom;
      const coordinateSystemHeight = (coordinateSystem.height - verticalMargin) * bandwidthFactor + verticalMargin;
      const updatedCoordinateSystem = { ...coordinateSystem, height: coordinateSystemHeight };
      return {
        yScale: createYScaleWithDimensions(updatedDimensions),
        xScale: createXScaleWithDimensions(updatedDimensions),
        coordinateSystem: updatedCoordinateSystem,
        dimensions: updatedDimensions,
        orientation
      };
    }
  }
  return {
    coordinateSystem,
    dimensions,
    orientation,
    yScale: defaultYScale,
    xScale: defaultXScale
  };
};

export const mapLegendOptionsToSortOrder = (selectedSortOption) => {
  if (selectedSortOption === LegendSortOptions.Asc) {
    return SortOrder.alphaNumericAscending;
  } else if (selectedSortOption === LegendSortOptions.Desc) {
    return SortOrder.alphaNumericDescending;
  } else if (selectedSortOption === LegendSortOptions.LargestToSmallest) {
    return SortOrder.numericAscending;
  } else {
    return SortOrder.numericDescending;
  }
};
